/* ============================================================================
   LA SESIÓN, DESDE EL NAVEGADOR

   Tres llamadas y un estado. Las tres contra los endpoints que ya existen
   (`server/apiSesion.js`): entrar, saber quién está adentro y cerrar.

   ═══ «NO HAY SERVIDOR» NO ES «NO ENTRÓ» ═══

   Son dos cosas distintas y confundirlas deja al usuario en una pantalla de
   ingreso que nunca va a funcionar:

   · **`afuera`** — hay servidor y hay base, y no hay sesión. Le toca el
     formulario.
   · **`sin-servidor`** — no hay con quién hablar (sin base configurada, sin
     red, o el servidor caído). **La aplicación sigue andando como siempre**, con
     la vista de operación: es para lo que está hecha, y un piloto en un iPad sin
     backend no puede quedarse mirando un formulario.

   Por eso este módulo nunca lanza: devuelve el estado, y quien llama decide.
   ========================================================================== */

const RUTA = "/api/sesion";

export const SIN_SERVIDOR = "sin-servidor";
export const ADENTRO = "adentro";
export const AFUERA = "afuera";

async function pedir( cuerpo = null, metodo = "GET" ) {
  const opciones = {
    method: metodo,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  };
  if ( cuerpo ) opciones.body = JSON.stringify( cuerpo );

  const respuesta = await fetch( RUTA, opciones );
  let datos = null;
  try {
    datos = respuesta.status === 204 ? null : await respuesta.json();
  } catch {
    datos = null;
  }
  return { ok: respuesta.ok, estado: respuesta.status, datos };
}

/**
 * Quién está adentro.
 *
 * Siempre devuelve un objeto: la aplicación no puede depender de que este
 * llamado funcione para arrancar.
 */
export async function estadoDeSesion() {
  try {
    const r = await pedir();
    if ( r.ok && r.datos?.usuario ) {
      return { estado: ADENTRO, usuario: r.datos.usuario, expira: r.datos.expira || null };
    }
    /* ═══ SOLO UN 401 CLARO SIGNIFICA «AFUERA» ═══

       Es la única respuesta en la que el servidor está diciendo, con todas las
       letras, «hay base y no hay sesión». Cualquier otra cosa —503 sin base
       configurada, un 500, un proxy que devuelve HTML, una respuesta que no es
       JSON— deja al usuario en un formulario que nunca va a funcionar.

       Y el precio de equivocarse es asimétrico: abrir la vista de operación de
       más no rompe nada —es lo que hace la aplicación sin backend desde
       siempre—, mientras que una entrada que no puede funcionar deja al piloto
       afuera de su herramienta. */
    if ( r.estado === 401 ) return { estado: AFUERA };
    return { estado: SIN_SERVIDOR, motivo: r.datos?.error || null };
  } catch {
    return { estado: SIN_SERVIDOR, motivo: "No se pudo hablar con el servidor." };
  }
}

/** Entrar. Devuelve `{ ok, usuario }` o `{ ok: false, motivo }` con el texto que
    hay que mostrar tal cual: el servidor ya lo escribió para que se entienda. */
export async function ingresar( email, clave ) {
  try {
    const r = await pedir( { email, clave }, "POST" );
    if ( r.ok && r.datos?.usuario ) return { ok: true, usuario: r.datos.usuario };
    return { ok: false, motivo: r.datos?.error || "No se pudo entrar.", estado: r.estado };
  } catch {
    return { ok: false, motivo: "No se pudo hablar con el servidor. Revisa la conexión." };
  }
}

/** Cerrar. Se ignora el resultado a propósito: si la sesión ya no estaba, el
    navegador igual tiene que dejar de mandar la cookie. */
export async function salir() {
  try {
    await pedir( null, "DELETE" );
    return true;
  } catch {
    return false;
  }
}
